<template>
  <section
    :id="`${cms.scroll_id}`"
    v-editable="cms"
    :class="[name, spacing, { 'is-hidden': isHidden }]"
    :style="mainCssVars"
  >
    <div
      class="full-screen-menu"
      :class="{ 'full-screen-menu-open': showMenu }"
    >
      <div class="inner-container">
        <div
          class="close-icon"
          @click="
            () => {
              toggleMenu()
            }
          "
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="Group 137">
              <rect
                id="Rectangle 236"
                width="2.57128"
                height="25.7128"
                transform="matrix(0.707103 -0.70711 0.707103 0.70711 0 1.81818)"
                fill="black"
              />
              <rect
                id="Rectangle 237"
                width="2.57128"
                height="25.7128"
                transform="matrix(-0.707103 -0.70711 0.707103 -0.70711 1.81641 20)"
                fill="black"
              />
            </g>
          </svg>
        </div>
        <ul :class="[`${name}__menu-list`, 'menu-list']">
          <li v-for="(item, index) in cms.links" :key="index">
            <component
              :is="item.component"
              v-editable="item"
              :cms="item"
              :class="`${name}__menu-link`"
            />
          </li>
        </ul>
      </div>
    </div>
    <grid
      :class="['grid--container', `${name}__grid`]"
      :layout="[
        {
          size: 1,
          columns: 'repeat(12, 1fr)',
          rows: 'repeat(1, 1fr)',
          gap: '0 6px',
        },
        {
          size: 2,
          columns: 'repeat(24, 1fr)',
          rows: 'repeat(1, 1fr)',
          gap: '10px',
        },
      ]"
    >
      <grid-item
        :class="[`${name}__grid-item`, `${name}__logo`]"
        :layout="[
          { size: 1, x: 1, width: 3 },
          { size: 3, x: 1, width: 3 },
        ]"
      >
        <a-link-v2 :url="href" :class="[`${name}__logo-link`]">
          <component
            :is="item.component"
            v-for="item in cms.logo"
            :key="item._uid"
            v-editable="item"
            :cms="{ ...item, copy: null }"
            :class="`${name}__logo-svg`"
            aria-hidden="true"
            focusable="false"
          />
        </a-link-v2>
      </grid-item>
      <grid-item :layout="[{ size: 1, x: 24, width: 1 }]">
        <div
          class="menu-open-button"
          @click="
            () => {
              toggleMenu()
            }
          "
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="Icon-Navigation">
              <circle id="Ellipse 6" cx="20" cy="7" r="3" fill="white" />
              <circle id="Ellipse 7" cx="20" cy="20" r="3" fill="white" />
              <circle id="Ellipse 8" cx="20" cy="33" r="3" fill="white" />
            </g>
          </svg>
        </div>
      </grid-item>
    </grid>
  </section>
</template>

<script>
import Href from '@/mixins/href'
import data from '~/components/organisms/o-site-header/o-site-header-v2.data'
import Colors from '~/mixins/colors'
import ASvgLogo from '~/components/atoms/a-svgs/a-svg-logo'
import ACtaV2 from '~/components/atoms/a-ctas/a-cta-v2'
import ALinkV2 from '~/components/atoms/a-links/a-link-v2'
export default {
  components: { ASvgLogo, ACtaV2, ALinkV2 },
  mixins: [Colors, Href],
  props: {
    cms: {
      type: Object,
      default() {
        const config = {}
        return process.env.NODE_ENV === 'development'
          ? { ...data, ...config }
          : config
      },
    },
  },

  data() {
    return {
      name: 'o-site-header-v2',
      active: false,
      isHidden: false,
      lastScrollY: 0,
      headerHeight: 0,
    }
  },

  computed: {
    showMenu() {
      return this.active
    },
    spacing() {
      return this.cms.spacing
    },
    mainCssVars() {
      return {
        '--bg-color': this.setColor(
          (this.cms.color_background || {}).color || 'black',
          this.cms.color_gradient
        ),
        '--text-color': this.setColor(
          (this.cms.color_text || {}).color || 'white'
        ),
        '--hover-color': this.setColor(
          (this.cms.color_hover || {}).color || '#C33CA8'
        ),
        '--disabled-color': this.setColor(
          (this.cms.color_disabled || {}).color || '#888b8d'
        ),
      }
    },
  },

  mounted() {
    this.headerHeight = this.$el.offsetHeight
    window.addEventListener('scroll', this.handleScroll)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    toggleMenu() {
      this.active = !this.active
    },
    handleScroll() {
      const currentScrollY = window.scrollY
      // Hide if scrolling down past the header
      if (
        currentScrollY > this.headerHeight &&
        currentScrollY > this.lastScrollY
      ) {
        this.isHidden = true
        document.body.style.paddingTop = '0px !important'
      } else {
        // Show if scrolling up or at top
        this.isHidden = false
        document.body.style.paddingTop = '73px !important'
      }
      this.lastScrollY = currentScrollY
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.o-site-header-v2 {
  width: 100%;
  min-height: 73px;
  background: var(--bg-color);
  border-color: var(--text-color);
  color: var(--text-color);
  position: fixed;
  top: 0;
  z-index: 5000;
  align-items: center;
  transition: transform 0.3s ease; // for smooth show/hide transition

  &.is-hidden {
    transform: translateY(-100%);
  }
}

.grid--container {
  align-items: center !important;
}

.full-screen-menu {
  position: absolute;
  width: 100vw;
  height: 100vh;
  transform: translateY(-100%);

  /* Let’s animate the transform property */
  transition: transform 0.5s ease-out;
}

.full-screen-menu-open {
  transform: translateY(0);
}

.inner-container {
  background-color: white;
  color: black;
  align-items: center;
  text-align: center;
  padding: 40px;
  margin: 20px;
  margin-right: 40px;
  position: relative;
  display: flex;
  height: calc(100vh - 40px);
  justify-content: center;
}

.menu-list {
  font-family: 'Druk', Helvetica, Arial, sans-serif;
  font-size: 120px;
  font-style: normal;
  font-weight: 500;
  line-height: 80%;
  text-transform: uppercase;
  list-style-type: none;

  li {
    margin-bottom: 10px;
  }

  li:hover {
    color: #ff3476;
  }
}

.close-icon {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  cursor: pointer;
  border: 0;
}

.menu-open-button {
  background: none;
  cursor: pointer;
  border: 0;
  width: 40px;
  height: 40px;
}

@media only screen and (max-width: 600px) {
  .menu-list {
    font-family: 'Druk', Helvetica, Arial, sans-serif;
    font-size: 80px;
    font-style: normal;
    font-weight: 500;
    line-height: 80%;
    text-transform: uppercase;
    list-style-type: none;
    margin-left: 0;
    padding-left: 0;
  }
}
</style>
